import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isReloading: boolean;
}

// Matches JSON parse errors across Chrome, Firefox, and Safari.
// Chrome/Firefox: "Unexpected token", "is not valid JSON"
// Safari: "JSON Parse error: Unexpected character"
const isJsonParseError = (msg?: string) =>
  !!msg && (
    msg.includes('is not valid JSON') ||
    msg.includes('Unexpected token') ||
    msg.includes('JSON Parse error')
  );

// Browser auto-translation (Safari/Chrome translate) can mutate the DOM under
// React's feet, causing React reconciliation to hit NotFoundError at
// removeChild (DOMException code 8). See ERI-700 and React issue #11538.
const isRemoveChildError = (error: Error) => {
  if (error.name === 'NotFoundError') return true;
  const domErr = error as unknown as { code?: number };
  if (typeof domErr.code === 'number' && domErr.code === 8) return true;
  const msg = error.message || '';
  return msg.includes('The object can not be found here') ||
    msg.includes('The node to be removed is not a child');
};

// Stale deployment: an old index.html references hashed JS chunks (e.g. the
// lazily-imported map) that no longer exist after a new deploy, so the dynamic
// import rejects. Cross-browser message variants. See ERI-1249.
const isChunkLoadError = (msg?: string) =>
  !!msg && (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );

const JSON_RELOAD_KEY = 'mw_stale_deploy_reload';
const REMOVE_CHILD_RELOAD_KEY = 'mw_remove_child_reload';
const CHUNK_RELOAD_KEY = 'mw_chunk_reload';
const RELOAD_GUARD_MS = 10_000;

const canReload = (key: string): boolean => {
  const last = sessionStorage.getItem(key);
  return !last || Date.now() - parseInt(last, 10) > RELOAD_GUARD_MS;
};

// Leaving a page cancels its in-flight imports, and Safari reports that as
// "Importing a module script failed", the same message as a stale deploy.
// Reloading then pulls the user back to the page they were leaving (ERI-2764),
// so a reload asked for while the page is being left waits. If the page is
// still open a few seconds later, the navigation was cancelled and the reload
// goes ahead.
const LEAVE_GRACE_MS = 3000;
let leavingPage = false;
let reloadAfterLeave: (() => void) | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    leavingPage = true;
    setTimeout(() => {
      leavingPage = false;
      const reload = reloadAfterLeave;
      reloadAfterLeave = null;
      reload?.();
    }, LEAVE_GRACE_MS);
  });
}

// Returns the sessionStorage guard key for a recoverable auto-reload error,
// or null if the error isn't a kind we silently reload on. All three classes
// are transient stale-state / DOM-race errors that a single reload clears.
const reloadKeyFor = (error: Error): string | null => {
  if (isJsonParseError(error.message)) return JSON_RELOAD_KEY;
  if (isChunkLoadError(error.message)) return CHUNK_RELOAD_KEY;
  if (isRemoveChildError(error)) return REMOVE_CHILD_RELOAD_KEY;
  return null;
};

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isReloading: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Recoverable stale-state errors (stale-deploy JSON, chunk-load failure,
    // translator/DOM-mutation removeChild) should silently auto-reload on the
    // first hit and only surface the error UI if the loop guard says we've
    // already retried.
    const key = reloadKeyFor(error);
    if (key) {
      return { hasError: true, error, isReloading: canReload(key) };
    }
    return { hasError: true, error, isReloading: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    // Transient stale-state errors (stale-deploy JSON / chunk-load failure, or
    // a browser-translator DOM race) usually clear on a single reload: it picks
    // up the fresh index.html + current chunk hashes, or a clean un-mutated DOM.
    // Guard against infinite reload loops with sessionStorage.
    const reloadKey = reloadKeyFor(error);
    if (reloadKey && canReload(reloadKey)) {
      const reload = () => {
        sessionStorage.setItem(reloadKey, String(Date.now()));
        // Short flush — don't keep the user waiting on a blank screen
        Sentry.flush(500).finally(() => window.location.reload());
      };
      if (leavingPage) reloadAfterLeave = reload;
      else reload();
      return;
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, isReloading: false });
  };

  public render() {
    // JSON parse error from a stale deployment — auto-reload is in flight.
    // Return null so the user never sees the error UI.
    if (this.state.isReloading) {
      return null;
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground">
                An unexpected error occurred. Please try refreshing the page.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReload} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </Button>
              <Button variant="outline" onClick={this.handleReset}>
                Try Again
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-6 p-4 bg-secondary/50 rounded-lg text-left">
                <p className="text-sm font-mono text-destructive break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
